import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import { MODULES } from '../constants';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import FAQSection from '../components/FAQSection';
import { trackEvent } from '../components/analytics';
import ImageCarousel, { usePublicImages } from '../components/ImageCarousel';

const SMSPage: React.FC = () => {
  const moduleData = MODULES.find(m => m.id === 'sms')!;

  const smsFAQ = [
    {
  question: "How does AviaGov support regulatory audits?",
  answer: "AviaGov provides audit-ready evidence through immutable audit logs, dashboard snapshots, compliance packs, and traceable links between hazards, risks, actions, and outcomes, reducing audit preparation time and uncertainty."
},
      {
        question: "Is the SMS module ECCAIRS 2 compatible?",
        answer: "Yes. AviaGov SMS supports the ECCAIRS 2 taxonomy and prepares compliant exports for EASA and national authority reporting, subject to  organizational review and approval."
      },
      {
        question: "Can staff report safety issues anonymously?",
        answer: "Yes. It supports ICAO Just Culture principles with anonymous reporting options that protect identity while preserving critical safety details for oversight."
      },
      {
        question: "Does it support Bow-Tie risk modeling?",
        answer: "Yes. The built-in Bow-Tie designer visualizes threats, barriers, and recovery paths to support risk decisions; final risk acceptance remains with accountable managers."
      }
    ];

    const featureOverrides: Record<string, string> = {
      'Mobile Reporting': 'Offline-ready occurrence reporting that preserves data integrity, confidentiality, and audit traceability, ensuring reportable events are captured even in degraded operational conditions.',
    };

    const smsCarouselImages = usePublicImages('sms', 'SMS interface screen');

    return (
    <div className="animate-fade-in-up">
      <SEO
        title="Aviation Safety Management System Software"
        description="ICAO Annex 19-compliant Safety Management System for airlines, operators, training organizations, and authorities, supporting audit-ready safety oversight and regulatory reporting."
        ogType="product"
        faq={smsFAQ}
      />

        {/* HERO */}
      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <nav className="flex items-center space-x-2 text-slate-400 text-sm mb-6 uppercase tracking-widest">
              <span>Modules</span>
              <span>/</span>
              <span className="text-success">SMS</span>
            </nav>

            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              Aviation safety <br />
              <span className="text-success">management system software.</span>
            </h1>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-6">
              Regulator-Aligned Safety Oversight for Aviation Organizations
            </p>

            <GlowCard className="p-8 bg-slate-900/60 border-white/10">
              <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                AviaGov SMS is a regulator-grade Safety Management Infrastructure that
                continuously assures compliance, risk control effectiveness, and executive
                accountability across ICAO, EASA, FAA, and EU 376/2014 frameworks.
              </p>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                Designed for airlines, operators, ATO/DTOs, and authorities
                implementing or overseeing an ICAO Annex 19 Safety Management
                System.
              </p>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                The system supports airline operations including occurrence
                reporting, investigations, audits, CAPA tracking, and
                regulator-ready evidence.
              </p>
              <p className="text-sm text-slate-400 leading-relaxed">
                When combined with the LMS module, safety findings can trigger
                targeted training actions, and completed training feeds back
                into the SMS as mitigation and safety promotion evidence.
              </p>
            </GlowCard>
          </div>

          {/* Visual / metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-success/10 border border-success/20 p-8 rounded-2xl text-center">
              <div className="text-4xl font-bold text-success mb-2">Always</div>
              <p className="text-xs text-slate-400 uppercase">Audit-Ready</p>
            </div>
              {/* <div className="bg-success/10 border border-success/20 p-8
  rounded-2xl text-center">
                <div className="text-4xl font-bold text-success mb-2">60%</div>
                <p className="text-xs text-slate-400 uppercase">
                  Faster Processing
                </p>
              </div> */}
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl text-center">
                <div className="text-4xl font-bold text-white mb-2">Regulation</div>
                <p className="text-xs text-slate-400 uppercase">
                  ICAO, EASA, FAA, and EU 376/2014 frameworks. Ready
                </p>
            </div>
            <div className="col-span-2 relative p-1 bg-slate-800 rounded-2xl overflow-hidden border border-white/10">
              <AIImage
                prompt="A vibrant 3D cartoon safety dashboard with green checkmarks and 3D radar spheres, neon highlights, data visualization."
                alt="Safety Analysis Dashboard"
                className="w-full opacity-90"
                aspectRatio="16:9"
                src="/smsDashboard.png"
                width={1600}
                height={900}
                sizes="(min-width: 1024px) 50vw, 100vw"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>

        {/* MENTAL MODEL */}
      <section className="py-12 px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-4 text-white">How Safety Flows in AviaGov</h2>
          <GlowCard className="max-w-7xl mx-auto">
            <p className="text-slate-400 text-base">
              In AviaGov SMS, safety flows from identification to executive and regulatory
              assurance:
              <br />
              report → assess → mitigate → verify → assure → demonstrate.
            </p>
          </GlowCard>
        </div>
      </section>

        {/* FEATURES */}
      <section className="py-24 px-4 border-b border-white/5">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-4 text-white">Core SMS Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mx-20">
            {moduleData.features.map((feature, i) => (
              <GlowCard key={i} className="hover:border-success/40">
                <h3 className="text-2xl font-bold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {featureOverrides[feature.title] || feature.description}
                </p>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

        <ImageCarousel
          title="SMS Interface Preview"
          description="Screens from the SMS workspace, evidence views, and reporting workflows."
          images={smsCarouselImages}
        />

        {/* FEATURE COVERAGE CTA */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <GlowCard className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Explore the full SMS feature coverage</h3>
              <p className="text-slate-400 text-sm">
                View web SMS workflows, the SMS public portal, and the planned mobile app in one list.
              </p>
            </div>
            <Link
              to="/modules/sms/features"
              className="inline-flex px-8 py-3 bg-accent text-primary font-bold rounded-xl"
              onClick={() => trackEvent('demo_request', { form_id: 'sms_features_section_cta', module: 'sms' })}
            >
              View SMS Features
            </Link>
          </GlowCard>
        </div>
      </section>

        {/* REGULATOR CREDIBILITY */}
      <section className="py-12 px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-4 text-white">Built for Regulatory Oversight</h2>
          <GlowCard className="max-w-7xl mx-auto">
            <p className="text-sm text-slate-400 leading-relaxed">
              AviaGov SMS is built to withstand regulatory scrutiny.
              Every safety report, risk decision, corrective action, and acceptance is
              traceable, time-bound, and defensible, with evidence available on demand
              for authority oversight.
            </p>
            <p className="text-sm text-slate-400 leading-relaxed mt-4">
              The same SMS framework also supports ANSP and aerodrome safety
              programs, including change assessments and safety case management,
              without altering governance boundaries.
            </p>
          </GlowCard>
        </div>
      </section>

        {/* RESPONSIBILITY */}
      <section className="py-12 px-4">
        <GlowCard className="max-w-7xl mx-auto">
          <h3 className="text-lg text-slate-300 text-center">
            AviaGov SMS does not replace organizational responsibility.
            All safety decisions, risk acceptances, and corrective actions remain under
            the authority of the organization.
            AviaGov provides the structure, traceability, and assurance mechanisms
            required to demonstrate those decisions to regulators.
          </h3>
        </GlowCard>
      </section>

      <FAQSection items={smsFAQ} title="Safety Management FAQ" />
    </div>
  );
};

export default SMSPage;
